import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import api from "../../services/api";

// "2026-10-01" <-> Date local (sem converter para UTC, que mudaria o dia)
const toDate = (value) => {
  if (!value) return null;
  const [y, m, d] = value.substring(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
};

const toApiDate = (date) => {
  if (!date) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`;
};

const formatDate = (value) =>
  value ? value.substring(0, 10).split("-").reverse().join("/") : "";

const errorDetail = (err, fallback) => {
  const errors = err?.response?.data?.errors;
  return errors ? Object.values(errors).flat()[0] : fallback;
};

/**
 * Técnicos, o reserva e a ausência de cada um. O reserva assume os chamados
 * do técnico quando ele está ausente (dentro do período) ou recusa o chamado.
 */
export default function Tecnicos() {
  const toast = useRef(null);
  const queryClient = useQueryClient();
  const [savingId, setSavingId] = useState(null);

  const [absenceTech, setAbsenceTech] = useState(null);
  const [absenceRange, setAbsenceRange] = useState(null);
  const [savingAbsence, setSavingAbsence] = useState(false);

  const { data: techs = [], isLoading } = useQuery(["tech-link-techs"], () =>
    api.get("/tech_links/techs").then((r) => r.data)
  );

  const refresh = () => {
    queryClient.invalidateQueries(["tech-link-techs"]);
    queryClient.invalidateQueries(["tech-link"]);
  };

  const showToast = (severity, detail) =>
    toast.current?.show({ severity, summary: "", detail, life: 4000 });

  const saveBackup = async (tech, backupTechId) => {
    setSavingId(tech.id);
    try {
      await api.put(`/tech_links/techs/${tech.id}/backup`, {
        backup_tech_id: backupTechId ?? null
      });
      showToast("success", `Reserva de ${tech.name} atualizado.`);
      refresh();
    } catch (err) {
      showToast("error", errorDetail(err, "Não foi possível atualizar o reserva."));
    } finally {
      setSavingId(null);
    }
  };

  const openAbsence = (tech) => {
    setAbsenceTech(tech);
    setAbsenceRange(
      tech.absence_start
        ? [toDate(tech.absence_start), toDate(tech.absence_end)]
        : null
    );
  };

  const saveAbsence = async (range) => {
    const [start, end] = range || [];
    if (range && (!start || !end)) {
      showToast("warn", "Selecione a data de início e a data de fim.");
      return;
    }

    setSavingAbsence(true);
    try {
      await api.put(`/tech_links/techs/${absenceTech.id}/absence`, {
        absence_start: toApiDate(start),
        absence_end: toApiDate(end)
      });
      showToast(
        "success",
        range
          ? `Ausência de ${absenceTech.name} registrada.`
          : `Ausência de ${absenceTech.name} removida.`
      );
      refresh();
      setAbsenceTech(null);
    } catch (err) {
      showToast("error", errorDetail(err, "Não foi possível salvar a ausência."));
    } finally {
      setSavingAbsence(false);
    }
  };

  const backupBody = (tech) => (
    <Dropdown
      value={tech.backup_tech_id}
      options={techs
        .filter((t) => t.id !== tech.id)
        .map((t) => ({ label: t.name, value: t.id }))}
      onChange={(e) => saveBackup(tech, e.value)}
      placeholder="Sem reserva"
      filter
      showClear
      disabled={savingId === tech.id}
      style={{ width: "100%" }}
    />
  );

  const nameBody = (tech) => (
    <div className="d-flex align-items-center" style={{ gap: 8 }}>
      {tech.name}
      {tech.is_absent && (
        <Badge
          value="Ausente"
          style={{ backgroundColor: "#9E3131", color: "white" }}
        />
      )}
    </div>
  );

  const absenceBody = (tech) => (
    <div className="d-flex align-items-center" style={{ gap: 8 }}>
      <span style={{ color: tech.absence_start ? undefined : "#6c757d" }}>
        {tech.absence_start
          ? `${formatDate(tech.absence_start)} a ${formatDate(tech.absence_end)}`
          : "Sem ausência"}
      </span>
      <Button
        icon="pi pi-calendar"
        tooltip="Definir ausência"
        tooltipOptions={{ position: "top" }}
        className="p-button-rounded p-button-text"
        onClick={() => openAbsence(tech)}
      />
    </div>
  );

  const absenceFooter = (
    <div>
      {absenceTech?.absence_start && (
        <Button
          label="Remover ausência"
          icon="pi pi-trash"
          className="p-button-text p-button-danger"
          onClick={() => saveAbsence(null)}
          disabled={savingAbsence}
        />
      )}
      <Button
        label="Cancelar"
        className="p-button-text"
        onClick={() => setAbsenceTech(null)}
        disabled={savingAbsence}
      />
      <Button
        label="Salvar"
        icon="pi pi-check"
        onClick={() => saveAbsence(absenceRange)}
        loading={savingAbsence}
        disabled={!absenceRange}
      />
    </div>
  );

  return (
    <div className="p-3">
      <Toast ref={toast} position="bottom-right" />
      <h4 className="mb-1">Técnicos</h4>
      <p style={{ color: "#6c757d" }}>
        O reserva assume os chamados do técnico quando ele está ausente ou
        recusa o chamado. Durante o período de ausência, os chamados vão para o
        reserva; ao terminar, voltam para o técnico automaticamente.
      </p>
      <DataTable
        value={techs}
        loading={isLoading}
        dataKey="id"
        paginator
        rows={20}
        className="p-datatable-sm"
        emptyMessage="Nenhum técnico encontrado."
      >
        <Column header="Técnico" field="name" body={nameBody} sortable />
        <Column
          header="Técnico reserva"
          body={backupBody}
          style={{ width: "20rem" }}
        />
        <Column header="Ausência" body={absenceBody} style={{ width: "18rem" }} />
      </DataTable>

      <Dialog
        header={absenceTech ? `Ausência - ${absenceTech.name}` : "Ausência"}
        visible={!!absenceTech}
        style={{ width: "420px" }}
        modal
        footer={absenceFooter}
        onHide={() => setAbsenceTech(null)}
        closable={!savingAbsence}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label htmlFor="absence-range" style={{ fontWeight: 600 }}>
            Período (início e fim)
          </label>
          <Calendar
            id="absence-range"
            value={absenceRange}
            onChange={(e) => setAbsenceRange(e.value)}
            selectionMode="range"
            dateFormat="dd/mm/yy"
            readOnlyInput
            showIcon
          />
          <small style={{ color: "#6c757d" }}>
            {absenceTech?.backup_tech
              ? `Durante o período, os chamados de ${absenceTech.name} vão para ${absenceTech.backup_tech.name}.`
              : `${absenceTech?.name ?? "O técnico"} não tem reserva definido: durante a ausência, os chamados dele ficam sem técnico para o ADM escolher.`}
          </small>
        </div>
      </Dialog>
    </div>
  );
}
